import React from 'react';
import type {SxProps} from '@mui/material';
import {InputLabel, Stack, useTheme} from '@mui/material';
import type {EntitySubType} from 'types';
import {Concept, EntitySubTypes} from 'types';
import type {DataLoadStatus} from 'api/types';
import {useLocalization} from 'localization';
import {UXUnit} from 'theme/settings';
import {Bar, CheckboxField, FlexBox} from 'components';
import type {SearchMainEntityType} from '../types';
import type {SearchCountResult} from '../hooks/useEntitySearchCount';
import {EntityButton} from './EntityButton';

type Props = {
  sx?: SxProps;
  // initialEntityType?: EntityType;
  subtypeRestriction?: EntitySubType[];
  selectedEntity?: SearchMainEntityType;
  selectedSubtype?: EntitySubType;
  onEntitySelected?: (entityType: SearchMainEntityType) => void;
  onSubEntitySelected?: (entitySubtype: EntitySubType) => void;
  entitySearchCount?: DataLoadStatus<SearchCountResult>;
  // Whether to include work search with International works. A limitation of
  // international works.
  includeInternationalWorks?: boolean;
  onChangeIncludeInternationalWorks?: (
    includeInternationalWorks: boolean,
  ) => void;
};

const SearchMainEntityTypes: SearchMainEntityType[] = [
  Concept.work,
  Concept.agent,
  Concept.collection,
  Concept.prize,
];

export const SearchEntitySelector: React.FC<Props> = ({
  sx,
  selectedEntity,
  selectedSubtype,
  onEntitySelected,
  onSubEntitySelected,
  entitySearchCount,
  includeInternationalWorks,
  onChangeIncludeInternationalWorks,
}) => {
  const {t} = useLocalization();
  const {colors} = useTheme();
  const labelSx: SxProps = {color: colors.burgundy};
  const subentities = selectedEntity
    ? EntitySubTypes[selectedEntity]
    : undefined;

  return (
    <FlexBox sx={sx}>
      <InputLabel sx={labelSx}>
        {t('extension.linkedValueSearch.showMe')}
      </InputLabel>
      <Stack direction={'row'} spacing={1}>
        {SearchMainEntityTypes.map(entityType => (
          <EntityButton
            key={entityType}
            type={entityType}
            isSelected={entityType === selectedEntity}
            onSelected={onEntitySelected}
          />
        ))}
        {subentities ? (
          <>
            <FlexBox px={0.5} center>
              <Bar height={2 * UXUnit} colorx={colors.burgundy} />
            </FlexBox>
            {subentities.map(subentity => (
              <EntityButton
                key={subentity}
                type={subentity}
                isSelected={subentity === selectedSubtype}
                onSelected={onSubEntitySelected}
                loading={entitySearchCount?.status === 'Loading'}
                count={entitySearchCount?.data?.[subentity] ?? undefined}
              />
            ))}
          </>
        ) : null}
        {selectedEntity === Concept.work ? (
          <>
            <FlexBox px={0.5} center>
              <Bar height={2 * UXUnit} colorx={colors.burgundy} />
            </FlexBox>
            <FlexBox px={0.5} center>
              <CheckboxField
                fieldId={'includeInternationalWorks'}
                name={'includeInternationalWorks'}
                value={includeInternationalWorks}
                boxLabel={t('extension.linkedValueSearch.international')}
                onChange={onChangeIncludeInternationalWorks}
              />
            </FlexBox>
          </>
        ) : null}
      </Stack>
    </FlexBox>
  );
};
